import { useState, useEffect, useCallback, useMemo } from "react";
import type { DisciplineSlug, KeyFindingContent, WordTypingContent, SequenceTypingContent } from "@jumpbean/shared";

// ==================== Types ====================

export type KeyboardLayout = "qwerty" | "abc";

export type KeyState =
  | "default"
  | "highlighted"
  | "pressed"
  | "correct"
  | "incorrect";

export interface ChildKeyboardProps {
  /** Challenge content - key finding, sequence typing, or word typing */
  content: KeyFindingContent | SequenceTypingContent | WordTypingContent;
  /** Discipline for theming (always typing, but kept for consistency) */
  discipline?: DisciplineSlug;
  /** Called when the answer is complete (single key or full word) */
  onAnswer: (answer: string) => void;
  /** Currently typed answer (for word typing) */
  selectedAnswer: string | null;
  /** Whether answer has been submitted */
  isSubmitted?: boolean;
  /** Whether the submitted answer was correct */
  isCorrect?: boolean;
  /** Keyboard layout preference */
  layout?: KeyboardLayout;
  /** Callback when layout changes */
  onLayoutChange?: (layout: KeyboardLayout) => void;
  /** Callback for sound effects */
  onKeyPress?: (key: string) => void;
}

// ==================== Keyboard Layouts ====================

const QWERTY_ROWS = [
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["Z", "X", "C", "V", "B", "N", "M"],
];

const ABC_ROWS = [
  ["A", "B", "C", "D", "E", "F", "G", "H", "I"],
  ["J", "K", "L", "M", "N", "O", "P", "Q", "R"],
  ["S", "T", "U", "V", "W", "X", "Y", "Z"],
];

// ==================== Component ====================

/**
 * ChildKeyboard - A magical, child-friendly on-screen keyboard
 *
 * Features:
 * - Large, colorful keys (80px+ touch targets)
 * - QWERTY and ABC layout options
 * - Glowing target key with pulsing animation
 * - Bouncy press animations
 * - Visual feedback for correct/incorrect
 * - Physical keyboard support
 *
 * Designed for Whiskers the Wizard Cat's "Magical Keyboard Kingdom"
 */
export function ChildKeyboard({
  content,
  discipline = "typing",
  onAnswer,
  selectedAnswer,
  isSubmitted = false,
  isCorrect = false,
  layout = "qwerty",
  onLayoutChange,
  onKeyPress,
}: ChildKeyboardProps) {
  // Track pressed keys for visual feedback
  const [pressedKey, setPressedKey] = useState<string | null>(null);
  const [keyStates, setKeyStates] = useState<Record<string, KeyState>>({});

  // Determine the challenge type
  const isKeyFinding = content.type === "key_finding";
  const isSequenceTyping = content.type === "sequence_typing";
  const isWordTyping = content.type === "word_typing";

  // Handle both camelCase (TypeScript type) and snake_case (database) field names
  const keyContent = content as KeyFindingContent & { target_key?: string };
  const wordContent = content as WordTypingContent & { target_word?: string };
  const seqContent = content as SequenceTypingContent & { target_sequence?: string[] };

  const targetKey = isKeyFinding
    ? (keyContent.targetKey ?? keyContent.target_key)?.toUpperCase() ?? null
    : null;

  // For sequence typing, join the array to form a "word" to type
  const targetSequence = isSequenceTyping
    ? (seqContent.targetSequence ?? seqContent.target_sequence)?.map(s => s.toUpperCase()) ?? null
    : null;

  const targetWord = isWordTyping
    ? (wordContent.targetWord ?? wordContent.target_word)?.toUpperCase() ?? null
    : isSequenceTyping && targetSequence
    ? targetSequence.join("")  // Treat sequence as a word for typing purposes
    : null;

  // Handle missing data gracefully
  if (isKeyFinding && !targetKey) {
    return (
      <div className="text-center p-8">
        <p className="text-neutral-600">Unable to load challenge. Please try again.</p>
      </div>
    );
  }
  if ((isWordTyping || isSequenceTyping) && !targetWord) {
    return (
      <div className="text-center p-8">
        <p className="text-neutral-600">Unable to load challenge. Please try again.</p>
      </div>
    );
  }

  // Current typed text (for word typing)
  const typedText = selectedAnswer?.toUpperCase() || "";

  // Get the next expected key for word typing
  const nextExpectedKey = useMemo(() => {
    if (isKeyFinding) return targetKey;
    if (!targetWord) return null;
    const nextIndex = typedText.length;
    return nextIndex < targetWord.length ? targetWord[nextIndex] : null;
  }, [isKeyFinding, targetKey, targetWord, typedText]);

  // Get keyboard rows based on layout
  const keyboardRows = layout === "qwerty" ? QWERTY_ROWS : ABC_ROWS;

  // Handle key press
  const handleKeyPress = useCallback((key: string) => {
    if (isSubmitted) return;

    const upperKey = key.toUpperCase();

    // Visual press effect
    setPressedKey(upperKey);
    setTimeout(() => setPressedKey(null), 150);

    // Sound effect callback
    onKeyPress?.(upperKey);

    if (isKeyFinding) {
      // For key finding, any key press is an answer
      onAnswer(upperKey);
    } else {
      // For word typing, append to current answer
      const newText = typedText + upperKey;
      onAnswer(newText.toLowerCase());
    }
  }, [isSubmitted, isKeyFinding, typedText, onAnswer, onKeyPress]);

  // Handle backspace for word typing
  const handleBackspace = useCallback(() => {
    if (isSubmitted || isKeyFinding || typedText.length === 0) return;
    const newText = typedText.slice(0, -1);
    onAnswer(newText.toLowerCase());
  }, [isSubmitted, isKeyFinding, typedText, onAnswer]);

  // Physical keyboard support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isSubmitted) return;

      // Only handle letter keys
      if (e.key.length === 1 && /[a-zA-Z]/.test(e.key)) {
        e.preventDefault();
        handleKeyPress(e.key);
      }

      // Handle backspace for word typing
      if (e.key === "Backspace" && !isKeyFinding) {
        e.preventDefault();
        handleBackspace();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyPress, handleBackspace, isSubmitted, isKeyFinding]);

  // Update key states when submitted
  useEffect(() => {
    if (!isSubmitted) {
      setKeyStates({});
      return;
    }

    const newStates: Record<string, KeyState> = {};

    if (isKeyFinding) {
      const pressedAnswer = selectedAnswer?.toUpperCase();
      if (pressedAnswer) {
        newStates[pressedAnswer] = isCorrect ? "correct" : "incorrect";
      }
      if (targetKey && !isCorrect) {
        newStates[targetKey] = "correct"; // Show correct key
      }
    } else if (targetWord) {
      // For word typing, highlight correct/incorrect letters
      for (let i = 0; i < typedText.length; i++) {
        const typedChar = typedText[i];
        const expectedChar = targetWord[i];
        if (typedChar && expectedChar) {
          newStates[typedChar] = typedChar === expectedChar ? "correct" : "incorrect";
        }
      }
    }

    setKeyStates(newStates);
  }, [isSubmitted, isCorrect, selectedAnswer, targetKey, targetWord, typedText, isKeyFinding]);

  // Get key state
  const getKeyState = (key: string): KeyState => {
    if (keyStates[key]) return keyStates[key];
    if (pressedKey === key) return "pressed";
    if (!isSubmitted && key === nextExpectedKey) return "highlighted";
    return "default";
  };

  return (
    <div className="flex flex-col items-center gap-6 w-full">
      {/* Word Display (for word typing) */}
      {!isKeyFinding && targetWord && (
        <WordDisplay
          targetWord={targetWord}
          typedText={typedText}
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
          imageUrl={(content as WordTypingContent).imageUrl}
        />
      )}

      {/* Target Key Display (for key finding) */}
      {isKeyFinding && targetKey && (
        <TargetKeyDisplay
          targetKey={targetKey}
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
        />
      )}

      {/* Keyboard */}
      <div
        className={`
          relative w-full max-w-[700px]
          bg-gradient-to-b from-whiskers-light/30 to-whiskers/20
          rounded-3xl p-4 md:p-6
          border-4 border-whiskers/30
          shadow-[0_8px_32px_rgba(74,144,217,0.2),inset_0_1px_0_rgba(255,255,255,0.5)]
        `}
      >
        {/* Magical sparkle decorations */}
        <div className="absolute -top-3 -left-3 text-2xl animate-sparkle" style={{ animationDelay: "0s" }}>✨</div>
        <div className="absolute -top-3 -right-3 text-2xl animate-sparkle" style={{ animationDelay: "0.3s" }}>✨</div>
        <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-2xl animate-sparkle" style={{ animationDelay: "0.6s" }}>⭐</div>

        {/* Keyboard rows */}
        <div className="flex flex-col gap-2 md:gap-3">
          {keyboardRows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className="flex justify-center gap-1.5 md:gap-2"
              style={{
                // Offset middle and bottom rows for QWERTY layout
                paddingLeft: layout === "qwerty" && rowIndex === 1 ? "24px" :
                             layout === "qwerty" && rowIndex === 2 ? "48px" : "0",
                paddingRight: layout === "qwerty" && rowIndex === 1 ? "24px" :
                              layout === "qwerty" && rowIndex === 2 ? "48px" : "0",
              }}
            >
              {row.map((key) => (
                <Key
                  key={key}
                  letter={key}
                  state={getKeyState(key)}
                  onPress={handleKeyPress}
                  disabled={isSubmitted}
                />
              ))}
            </div>
          ))}

          {/* Bottom row with backspace (only for word typing) */}
          {!isKeyFinding && (
            <div className="flex justify-center gap-2 mt-2">
              <button
                type="button"
                onClick={handleBackspace}
                disabled={isSubmitted || typedText.length === 0}
                className={`
                  flex items-center justify-center gap-2
                  px-6 py-3 min-h-touch
                  rounded-xl
                  font-display font-bold text-body-lg
                  transition-all duration-200
                  ${isSubmitted || typedText.length === 0
                    ? "bg-neutral-200 text-neutral-400 cursor-not-allowed"
                    : "bg-white hover:bg-neutral-50 text-neutral-700 border-2 border-neutral-300 hover:border-neutral-400 shadow-[0_3px_0_0_theme(colors.neutral.400)] hover:shadow-[0_2px_0_0_theme(colors.neutral.400)] hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-none"
                  }
                `}
                aria-label="Backspace"
              >
                <BackspaceIcon className="w-5 h-5" />
                <span className="hidden sm:inline">Back</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Layout toggle */}
      <LayoutToggle
        currentLayout={layout}
        onLayoutChange={onLayoutChange}
        disabled={isSubmitted}
      />
    </div>
  );
}

// ==================== Sub-components ====================

interface KeyProps {
  letter: string;
  state: KeyState;
  onPress: (key: string) => void;
  disabled?: boolean;
}

/**
 * Individual keyboard key with magical styling
 */
function Key({ letter, state, onPress, disabled = false }: KeyProps) {
  const isHighlighted = state === "highlighted";
  const isPressed = state === "pressed";
  const isCorrect = state === "correct";
  const isIncorrect = state === "incorrect";

  return (
    <button
      type="button"
      onClick={() => onPress(letter)}
      disabled={disabled}
      className={`
        relative
        w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-[72px] lg:h-[72px]
        rounded-xl md:rounded-2xl
        font-display font-bold
        text-xl sm:text-2xl md:text-3xl
        transition-all duration-150
        ${getKeyStyles(state, disabled)}
      `}
      aria-label={`Key ${letter}`}
      aria-pressed={isPressed}
    >
      {/* Glow effect for highlighted key */}
      {isHighlighted && !disabled && (
        <>
          <div className="absolute inset-0 rounded-xl md:rounded-2xl bg-whiskers-accent/50 animate-pulse" />
          <div className="absolute -inset-1 rounded-2xl md:rounded-3xl bg-whiskers-accent/30 animate-ping" style={{ animationDuration: "1.5s" }} />
        </>
      )}

      {/* Key face */}
      <span className={`
        relative z-10
        ${isHighlighted && !disabled ? "text-neutral-900 drop-shadow-[0_0_8px_rgba(255,215,0,0.8)]" : ""}
        ${isCorrect ? "text-white" : ""}
        ${isIncorrect ? "text-white" : ""}
      `}>
        {letter}
      </span>

      {/* Sparkle on highlighted key */}
      {isHighlighted && !disabled && (
        <span className="absolute -top-2 -right-2 text-lg animate-bounce">⭐</span>
      )}

      {/* Check mark for correct */}
      {isCorrect && (
        <span className="absolute -top-1 -right-1 text-base bg-white rounded-full w-6 h-6 flex items-center justify-center shadow-md">
          ✓
        </span>
      )}

      {/* X for incorrect */}
      {isIncorrect && (
        <span className="absolute -top-1 -right-1 text-base bg-white rounded-full w-6 h-6 flex items-center justify-center shadow-md text-fairy-600">
          ✗
        </span>
      )}
    </button>
  );
}

/**
 * Get styles for a key based on its state
 */
function getKeyStyles(state: KeyState, disabled: boolean): string {
  if (disabled && state === "default") {
    return `
      bg-neutral-200 border-2 border-neutral-300 text-neutral-400
      cursor-not-allowed
    `;
  }

  switch (state) {
    case "highlighted":
      return `
        bg-gradient-to-b from-whiskers-accent via-yellow-400 to-amber-500
        border-4 border-amber-600
        text-neutral-900
        shadow-[0_4px_0_0_theme(colors.amber.700),0_0_20px_rgba(255,215,0,0.5)]
        scale-105
        cursor-pointer
      `;

    case "pressed":
      return `
        bg-gradient-to-b from-whiskers-light to-whiskers
        border-4 border-whiskers-dark
        text-neutral-900
        shadow-[0_1px_0_0_theme(colors.whiskers.dark)]
        translate-y-1
        scale-95
      `;

    case "correct":
      return `
        bg-gradient-to-b from-meadow-400 to-meadow-600
        border-4 border-meadow-700
        text-neutral-900
        shadow-[0_4px_0_0_theme(colors.meadow.800)]
        animate-celebrate
      `;

    case "incorrect":
      return `
        bg-gradient-to-b from-fairy-400 to-fairy-600
        border-4 border-fairy-700
        text-neutral-900
        shadow-[0_4px_0_0_theme(colors.fairy.800)]
        animate-encourage
      `;

    default:
      return `
        bg-gradient-to-b from-white via-neutral-50 to-neutral-100
        border-3 border-neutral-300
        text-neutral-800
        shadow-[0_4px_0_0_theme(colors.neutral.400),0_6px_12px_rgba(0,0,0,0.08)]
        hover:from-whiskers-light/30 hover:via-whiskers-light/20 hover:to-whiskers-light/10
        hover:border-whiskers-light
        hover:-translate-y-0.5
        hover:shadow-[0_5px_0_0_theme(colors.neutral.400),0_8px_16px_rgba(0,0,0,0.1)]
        active:translate-y-1
        active:shadow-[0_1px_0_0_theme(colors.neutral.400)]
        cursor-pointer
      `;
  }
}

interface TargetKeyDisplayProps {
  targetKey: string;
  isSubmitted: boolean;
  isCorrect: boolean;
}

/**
 * Display for key finding challenges
 */
function TargetKeyDisplay({ targetKey, isSubmitted, isCorrect }: TargetKeyDisplayProps) {
  return (
    <div className="text-center">
      <p className="font-body text-body-lg text-neutral-600 mb-4">
        Find and press the letter:
      </p>
      <div
        className={`
          inline-flex items-center justify-center
          w-32 h-32 md:w-40 md:h-40
          rounded-3xl
          font-display font-bold
          text-display-xl md:text-[5rem]
          transition-all duration-300
          ${isSubmitted && isCorrect
            ? "bg-gradient-to-b from-meadow-300 to-meadow-500 text-neutral-900 border-4 border-meadow-600 shadow-[0_8px_0_0_theme(colors.meadow.700)] animate-celebrate"
            : isSubmitted && !isCorrect
            ? "bg-gradient-to-b from-fairy-300 to-fairy-500 text-neutral-900 border-4 border-fairy-600 shadow-[0_8px_0_0_theme(colors.fairy.700)] animate-encourage"
            : "bg-gradient-to-b from-whiskers-accent/80 to-amber-500 text-neutral-900 border-4 border-amber-600 shadow-[0_8px_0_0_theme(colors.amber.700),0_0_40px_rgba(255,215,0,0.4)] animate-float"
          }
        `}
      >
        {targetKey}
        {!isSubmitted && (
          <span className="absolute -top-4 -right-4 text-3xl animate-sparkle">✨</span>
        )}
      </div>
      {isSubmitted && (
        <p className={`mt-4 font-display font-bold text-body-xl ${isCorrect ? "text-meadow-600" : "text-fairy-600"}`}>
          {isCorrect ? "Perfect! You found it! 🎉" : "Almost! Try the glowing key! ✨"}
        </p>
      )}
    </div>
  );
}

interface WordDisplayProps {
  targetWord: string;
  typedText: string;
  isSubmitted: boolean;
  isCorrect: boolean;
  imageUrl?: string;
}

/**
 * Display for word typing challenges
 */
function WordDisplay({ targetWord, typedText, isSubmitted, isCorrect, imageUrl }: WordDisplayProps) {
  return (
    <div className="text-center w-full max-w-lg">
      {/* Image if provided */}
      {imageUrl && (
        <div className="mb-4 flex justify-center">
          <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl overflow-hidden bg-neutral-100 border-4 border-neutral-200 shadow-lg">
            <img
              src={imageUrl}
              alt={`Picture of ${targetWord.toLowerCase()}`}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}

      <p className="font-body text-body-lg text-neutral-600 mb-3">
        Type the word:
      </p>

      {/* Target word with letter slots */}
      <div className="flex justify-center gap-2 mb-4">
        {targetWord.split("").map((letter, index) => {
          const typedLetter = typedText[index];
          const isTyped = !!typedLetter;
          const isCurrentPosition = index === typedText.length;
          const isLetterCorrect = typedLetter === letter;

          return (
            <div
              key={index}
              className={`
                w-12 h-14 md:w-16 md:h-20
                rounded-xl
                flex items-center justify-center
                font-display font-bold
                text-2xl md:text-3xl
                transition-all duration-200
                ${isSubmitted && isTyped
                  ? isLetterCorrect
                    ? "bg-meadow-400 text-neutral-900 border-4 border-meadow-600 shadow-[0_3px_0_0_theme(colors.meadow.700)]"
                    : "bg-fairy-400 text-neutral-900 border-4 border-fairy-600 shadow-[0_3px_0_0_theme(colors.fairy.700)]"
                  : isTyped
                  ? "bg-whiskers-light text-neutral-900 border-4 border-whiskers-dark shadow-[0_3px_0_0_theme(colors.whiskers.dark)]"
                  : isCurrentPosition
                  ? "bg-white border-4 border-whiskers-accent shadow-[0_3px_0_0_theme(colors.amber.600),0_0_12px_rgba(255,215,0,0.4)] animate-pulse"
                  : "bg-white border-4 border-neutral-300 shadow-[0_3px_0_0_theme(colors.neutral.400)]"
                }
              `}
            >
              {typedLetter || (
                <span className="text-neutral-300 text-lg">_</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Progress indicator */}
      <div className="flex justify-center gap-1 mb-2">
        {targetWord.split("").map((_, index) => (
          <div
            key={index}
            className={`
              w-3 h-3 rounded-full
              transition-all duration-200
              ${index < typedText.length
                ? "bg-whiskers scale-110"
                : "bg-neutral-300"
              }
            `}
          />
        ))}
      </div>

      {/* Feedback message */}
      {isSubmitted && (
        <p className={`mt-4 font-display font-bold text-body-xl ${isCorrect ? "text-meadow-600" : "text-fairy-600"}`}>
          {isCorrect ? "Amazing spelling! 🌟" : "So close! Try again! 💪"}
        </p>
      )}
    </div>
  );
}

interface LayoutToggleProps {
  currentLayout: KeyboardLayout;
  onLayoutChange?: (layout: KeyboardLayout) => void;
  disabled?: boolean;
}

/**
 * Toggle between QWERTY and ABC layouts
 */
function LayoutToggle({ currentLayout, onLayoutChange, disabled = false }: LayoutToggleProps) {
  const handleLayoutChange = (layout: KeyboardLayout) => {
    if (!disabled && onLayoutChange) {
      onLayoutChange(layout);
    }
  };

  return (
    <div className="flex items-center gap-3 text-sm font-body text-neutral-500">
      <span>Layout:</span>
      <div className="flex bg-neutral-200 rounded-full p-1">
        <button
          type="button"
          onClick={() => handleLayoutChange("qwerty")}
          disabled={disabled || !onLayoutChange}
          className={`
            px-3 py-1 rounded-full transition-all duration-200
            ${currentLayout === "qwerty"
              ? "bg-whiskers-dark text-white shadow-sm"
              : "text-neutral-600 hover:bg-neutral-300"
            }
            ${disabled || !onLayoutChange ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
          `}
          aria-pressed={currentLayout === "qwerty"}
          aria-label="Switch to QWERTY layout"
        >
          QWERTY
        </button>
        <button
          type="button"
          onClick={() => handleLayoutChange("abc")}
          disabled={disabled || !onLayoutChange}
          className={`
            px-3 py-1 rounded-full transition-all duration-200
            ${currentLayout === "abc"
              ? "bg-whiskers-dark text-white shadow-sm"
              : "text-neutral-600 hover:bg-neutral-300"
            }
            ${disabled || !onLayoutChange ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
          `}
          aria-pressed={currentLayout === "abc"}
          aria-label="Switch to ABC layout"
        >
          ABC
        </button>
      </div>
    </div>
  );
}

/**
 * Backspace icon
 */
function BackspaceIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
      <line x1="18" y1="9" x2="12" y2="15" />
      <line x1="12" y1="9" x2="18" y2="15" />
    </svg>
  );
}

export default ChildKeyboard;
